/**
 * Utility to print a Cancellation Slip
 * @param {Object} data - Contains plot, customer, booking, and cancellation details
 */
const printCancellationSlip = (data) => {
  const { plot, customer, booking, cancellation } = data;

  const societyName = plot?.society_id?.name || "AL NOOR DEVELOPERS";
  const societyLocation = plot?.society_id?.location || "Lahore, Pakistan";

  const netRefund = (cancellation?.refunded_amount || 0) + (cancellation?.remaining_balance || 0);
  const totalPaid = (cancellation?.refunded_amount || 0) + (cancellation?.remaining_balance || 0) + (cancellation?.society_deduction_amount || 0) + (cancellation?.dealer_commission_deducted || 0);
  const totalDeductions = (cancellation?.society_deduction_amount || 0) + (cancellation?.dealer_commission_deducted || 0);

  const printWindow = window.open("", "_blank");
  printWindow.document.write(`
    <html>
      <head>
        <title>Cancellation Slip - ${plot?.plot_number || "N/A"}</title>
        <style>
          @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700&display=swap');
          body {
            font-family: 'Inter', sans-serif;
            line-height: 1.6;
            color: #1e293b;
            margin: 0;
            padding: 40px;
            background: #fff;
          }
          .header {
            text-align: center;
            margin-bottom: 40px;
            border-bottom: 2px solid #f1f5f9;
            padding-bottom: 20px;
          }
          .society-name {
            font-size: 28px;
            font-weight: 700;
            color: #0d9488;
            margin: 0;
            text-transform: uppercase;
            letter-spacing: 1px;
          }
          .society-loc {
            font-size: 14px;
            color: #64748b;
            margin: 5px 0 0 0;
          }
          .slip-title {
            text-align: center;
            font-size: 20px;
            font-weight: 700;
            margin: 20px 0;
            text-decoration: underline;
            color: #0f172a;
          }
          .section {
            margin-bottom: 30px;
          }
          .section-title {
            font-size: 14px;
            font-weight: 700;
            color: #0d9488;
            text-transform: uppercase;
            border-bottom: 1px solid #e2e8f0;
            padding-bottom: 5px;
            margin-bottom: 15px;
          }
          .grid {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 20px;
          }
          .info-item {
            display: flex;
            justify-content: space-between;
            margin-bottom: 8px;
            font-size: 14px;
          }
          .label {
            color: #64748b;
            font-weight: 400;
          }
          .value {
            font-weight: 600;
            color: #1e293b;
          }
          .financials {
            background: #f8fafc;
            padding: 20px;
            border-radius: 12px;
            border: 1px solid #e2e8f0;
          }
          .total-row {
            border-top: 1px solid #cbd5e1;
            margin-top: 10px;
            padding-top: 10px;
            font-size: 16px;
          }
          .total-value {
            color: #0d9488;
            font-size: 18px;
          }
          .footer {
            margin-top: 60px;
            display: flex;
            justify-content: space-between;
          }
          .signature-box {
            text-align: center;
            width: 200px;
          }
          .signature-line {
            border-top: 1px solid #1e293b;
            margin-bottom: 5px;
          }
          @media print {
            body { padding: 20px; }
            .financials { background: #f8fafc !important; -webkit-print-color-adjust: exact; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1 class="society-name">${societyName}</h1>
          <p class="society-loc">${societyLocation}</p>
        </div>

        <h2 class="slip-title">PLOT CANCELLATION & REFUND SLIP</h2>

        <div class="section">
          <h3 class="section-title">Customer Information</h3>
          <div class="grid">
            <div>
              <div class="info-item"><span class="label">Customer Name:</span> <span class="value">${customer?.name || "N/A"}</span></div>
              <div class="info-item"><span class="label">Father Name:</span> <span class="value">${customer?.contactInfo?.father_name || "N/A"}</span></div>
              <div class="info-item"><span class="label">CNIC:</span> <span class="value">${customer?.contactInfo?.cnic || "N/A"}</span></div>
            </div>
            <div>
              <div class="info-item"><span class="label">Phone:</span> <span class="value">${customer?.contactInfo?.phone || "N/A"}</span></div>
              <div class="info-item"><span class="label">Booking Date:</span> <span class="value">${new Date(booking?.booking_date).toLocaleDateString()}</span></div>
              <div class="info-item"><span class="label">Cancellation Date:</span> <span class="value">${new Date(cancellation?.createdAt).toLocaleDateString()}</span></div>
            </div>
          </div>
        </div>

        <div class="section">
          <h3 class="section-title">Plot Details</h3>
          <div class="grid">
            <div>
              <div class="info-item"><span class="label">Plot Number:</span> <span class="value">${plot?.plot_number || "N/A"}</span></div>
              <div class="info-item"><span class="label">Block:</span> <span class="value">${plot?.block || plot?.plot_id?.block || "N/A"}</span></div>
            </div>
            <div>
              <div class="info-item"><span class="label">Size:</span> <span class="value">${plot?.size || plot?.marla + " Marla" || "N/A"}</span></div>
              <div class="info-item"><span class="label">Category:</span> <span class="value">${plot?.category || "Standard"}</span></div>
            </div>
          </div>
        </div>

        <div class="section">
          <h3 class="section-title">Refund Breakdown</h3>
          <div class="financials">
            <div class="info-item"><span class="label">Total Amount Paid by Customer:</span> <span class="value">PKR ${totalPaid.toLocaleString()}</span></div>
            <div class="info-item" style="color: #ef4444;"><span class="label">Society Cancellation Deduction:</span> <span class="value">- PKR ${cancellation?.society_deduction_amount?.toLocaleString() || 0}</span></div>
            <div class="info-item" style="color: #ef4444;"><span class="label">Dealer Commission Recovery:</span> <span class="value">- PKR ${cancellation?.dealer_commission_deducted?.toLocaleString() || 0}</span></div>
            <div class="info-item" style="color: #ef4444; margin-bottom: 15px;"><span class="label">Total Deductions:</span> <span class="value">- PKR ${totalDeductions.toLocaleString()}</span></div>
            
            <div class="info-item total-row">
              <span class="label" style="font-weight: 700; color: #0f172a;">NET REFUNDABLE AMOUNT:</span> 
              <span class="value total-value">PKR ${netRefund.toLocaleString()}</span>
            </div>
            <div class="info-item">
              <span class="label">Refund Type:</span> 
              <span class="value" style="text-transform: capitalize;">${cancellation?.refund_type}</span>
            </div>
          </div>
        </div>

        <div class="section">
          <h3 class="section-title">Terms & Conditions</h3>
          <p style="font-size: 11px; color: #64748b;">
            1. This slip is a proof of cancellation and the finalized refund amount.<br/>
            2. Refund will be processed as per the chosen refund type (Full/Installment).<br/>
            3. In case of installments, a separate schedule will be provided.<br/>
            4. This document must be kept safe for future reference and actual refund collection.
          </p>
        </div>

        <div class="footer">
          <div class="signature-box">
            <div class="signature-line"></div>
            <div class="label">Authorized Signature</div>
          </div>
          <div class="signature-box">
            <div class="signature-line"></div>
            <div class="label">Customer Signature</div>
          </div>
          <div class="signature-box">
            <div class="signature-line"></div>
            <div class="label">Accounts Department</div>
          </div>
        </div>

        <script>
          window.onafterprint = function() { window.close(); };
          setTimeout(() => { window.print(); }, 500);
        </script>
      </body>
    </html>
  `);
  printWindow.document.close();
};

export default printCancellationSlip;
